import { User } from '../types';
import { parseJsonResponse } from '../utils/apiUtils';
import { getApiBaseUrl } from '../config/api';
import { AUTH_SEED } from '../config/api';
import { ReservationService } from './ReservationService';
import { montarValorAutorizacao, salvarTokenAutenticacaoCliente } from '../utils/authToken';

interface RespostaResetSenha {
  status: 'Sucesso' | 'Erro';
  message: string;
}

interface RespostaTokenResetSenha {
  status: 'success' | 'error';
  message: string;
  valid?: boolean;
}

export class AuthService {
  
  static async login(email: string, password: string): Promise<User> {
    // {|INICIO| 22/07/2026 às 22:50 - ETAPA 7: MIGRAR LOGIN PARA POST SEGURO E RECEBER TROCA OBRIGATORIA}
    if (!email.trim()) {
      throw new Error('O e-mail é obrigatório');
    }

    if (!password.trim()) {
      throw new Error('A senha é obrigatória');
    }

    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/loginservice`;
    
    try {
      const headers = new Headers();
      headers.append('Authorization', 'Basic ' + AUTH_SEED);
      headers.append('Content-Type', 'application/json');
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          acao: 'login',
          email: email.trim(),
          senha: password
        }),
        redirect: 'follow'
      });

      if (!response.ok) {
        let message = 'Não foi possível concluir o login. Tente novamente em instantes.';

        if (response.status === 401 || response.status === 403) {
          message = 'E-mail ou senha inválidos. Verifique os dados e tente novamente.';
        } else if (response.status === 404) {
          message = 'Serviço de login temporariamente indisponível. Tente novamente em instantes ou fale com a equipe da Granja Viana.';
        } else if (response.status >= 500) {
          message = 'Falha no servidor ao processar o login. Tente novamente em instantes.';
        }

        throw new Error(message);
      }

      const text = await response.text();

      let result;
      try {
        result = JSON.parse(text);
      } catch (parseError) {
        console.error('JSON parse error:', parseError);
        throw new Error('Resposta inválida do servidor');
      }
      
      // Check for success like original JS: result.data.status === 'Sucesso'
      if (result.data && result.data.status === 'Sucesso') {
        // {|INICIO| 13/07/2026 às 11:41 - PERSISTIR TOKEN DE CLIENTE NO LOGIN DO FRONT}
        const tokenAutenticacao = typeof result.data.token_autenticacao === 'string'
          ? result.data.token_autenticacao
          : '';
        // {|INICIO| 22/07/2026 às 14:10 - ETAPA 4: PARAR DE PERSISTIR CAMPOS SENSIVEIS DE LOGIN NO FRONT}
        const user: User = {
          id: result.data.id,
          idcliente: result.data.id, // Usar o mesmo ID para compatibilidade
          token_autenticacao: tokenAutenticacao || undefined,
          nome: result.data.nome,
          email: result.data.email,
          cpf_cnpj: result.data.cpf_cnpj,
          data_nascimento: result.data.data_nascimento || '',
          telefone_celular: result.data.telefone_celular || '',
          endereco: result.data.endereco || '',
          cidade: result.data.cidade || '',
          estado: result.data.estado || '',
          cep: result.data.cep || '',
          // {|INICIO| 22/07/2026 às 14:24 - ETAPA 4 AJUSTE: RESTAURAR SALDO DE CONTA ASSINADA NO LOGIN}
          conta_assinada: Number(result.data.conta_assinada) || 0,
          // {|FIM| 22/07/2026 às 14:24 - ETAPA 4 AJUSTE: RESTAURAR SALDO DE CONTA ASSINADA NO LOGIN}
          pontos: Number(result.data.pontos) || 0,
          min_vagas_bat_fechada: Number(result.data.min_vagas_bat_fechada) || 0,
          tipo_cliente: result.data.tipo_cliente || 'C',
          menor_de_idade: result.data.menor_de_idade || false,
          responsavel: result.data.responsavel || '',
          cpf_responsavel: result.data.cpf_responsavel || '',
          telefone_responsavel: result.data.telefone_responsavel || '',
          // {|INICIO| 22/07/2026 às 22:50 - ETAPA 7: PROPAGAR TROCA OBRIGATORIA DE SENHA NO LOGIN}
          exigir_troca_senha: Boolean(result.data.exigir_troca_senha)
          // {|FIM| 22/07/2026 às 22:50 - ETAPA 7: PROPAGAR TROCA OBRIGATORIA DE SENHA NO LOGIN}
        };
        
        // Store user data in localStorage like original JS
        localStorage.setItem('currentUser', JSON.stringify(user));
        localStorage.setItem('login', 'true');
        localStorage.setItem('login_id', result.data.id);
        localStorage.setItem('login_nome', result.data.nome);
        localStorage.setItem('login_cpf', result.data.cpf_cnpj);
        localStorage.setItem('login_email', result.data.email);
        localStorage.setItem('login_min_vagas_bat_fechada', result.data.min_vagas_bat_fechada);
        localStorage.setItem('login_pontos', result.data.pontos);
        localStorage.setItem('login_conta_assinada', String(result.data.conta_assinada || 0));
        salvarTokenAutenticacaoCliente(tokenAutenticacao || null);
        // {|FIM| 22/07/2026 às 14:10 - ETAPA 4: PARAR DE PERSISTIR CAMPOS SENSIVEIS DE LOGIN NO FRONT}
        // {|FIM| 13/07/2026 às 11:41 - PERSISTIR TOKEN DE CLIENTE NO LOGIN DO FRONT}
        // Dispara evento global para reagir no calendário e demais componentes
        try {
          window.dispatchEvent(new CustomEvent('userLogin', { detail: { user } }));
        } catch {}

        return user;
      } else {
        // Use the message from result.data.message like original JS
        throw new Error(result.data?.message || 'Erro ao fazer login');
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Erro de conexão. Verifique suas credenciais e tente novamente.');
    }
    // {|FIM| 22/07/2026 às 22:50 - ETAPA 7: MIGRAR LOGIN PARA POST SEGURO E RECEBER TROCA OBRIGATORIA}
  }

  static async cadastrarUsuario(userData: {
    nome: string;
    email: string;
    cpf_cnpj: string;
    telefone_celular: string;
    data_nascimento: string;
    endereco: string;
    cidade: string;
    estado: string;
    cep: string;
    senha: string;
  }): Promise<User> {
    try {
      // Comentário (function-level): Cria um novo usuário via ReservaRestService::onCriarUsuario
      // e, em caso de sucesso, realiza login automático para obter o objeto User completo.
      const apiResult = await ReservationService.cadastrarUsuario({
        nome: userData.nome,
        email: userData.email,
        cpf_cnpj: userData.cpf_cnpj,
        data_nascimento: userData.data_nascimento,
        telefone_celular: userData.telefone_celular,
        codigo_ibge_pais: 1058
      }) as any;

      // Estruturas esperadas:
      // Sucesso: { status: 'Sucesso', message: '...', data?: {...cliente...} }
      // Erro:    { status: 'Erro', message: 'mensagem específica...' }
      const status = apiResult?.status;
      const message: string | undefined = apiResult?.message || apiResult?.data?.message;

      if (!apiResult || !status) {
        throw new Error('Não foi possível concluir seu cadastro. Resposta inválida do servidor.');
      }

      if (String(status).toLowerCase() !== 'sucesso') {
        throw new Error(message || 'Não foi possível concluir seu cadastro. Tente novamente em instantes.');
      }

      // Cadastro OK: tenta login automático para seguir o fluxo normal (como se fosse login())
      const loggedUser = await AuthService.login(userData.email, userData.senha);
      return loggedUser;
    } catch (error) {
      console.error('Cadastro error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
  }

  static getCurrentUser(): User | null {
    const userData = localStorage.getItem('currentUser') || localStorage.getItem('kgv_user');
    return userData ? JSON.parse(userData) : null;
  }

  static async logout(): Promise<void> {
    try {
      // Import legacy helpers
      const { 
        exibirAlerta, 
        setValueLocalStorage, 
        atualizarVisibilidadeMenu, 
        fecharModal, 
        stopInactivityTimer, 
        colapsed, 
        onSelectDate, 
        obterDiasSemAgenda,
        clearLoginFields 
      } = await import('../utils/legacyHelpers');

      // Get current date from datepicker (simulation)
      const dateText = new Date(); // Simula $("#datepicker").datepicker("getDate")
      
      if (!dateText) {
        console.error("Nenhuma data selecionada no Datepicker.");
        return;
      }

      // Format initial date for verification
      const dataInicial = new Date(dateText);

      // Get disabled days
      await obterDiasSemAgenda(dataInicial);
      const disabledDays: number[] = []; // Simula _dayDisable

      // Check if initial date is disabled
      while (disabledDays.includes(dataInicial.getDate())) {
        // Increment to next day
        dataInicial.setDate(dataInicial.getDate() + 1);
      }

      // Format date to DD-MM-YYYY format
      const formattedDate = 
        String(dataInicial.getDate()).padStart(2, '0') + '-' +
        String(dataInicial.getMonth() + 1).padStart(2, '0') + '-' +
        dataInicial.getFullYear();

      // Show logout alert
      exibirAlerta('logoff');

      // Remove user information from localStorage
      // {|INICIO| 13/07/2026 às 11:41 - LIMPAR TOKEN DE CLIENTE NO LOGOUT}
      localStorage.removeItem('currentUser');
      localStorage.removeItem('kgv_user');
      salvarTokenAutenticacaoCliente(null);
      setValueLocalStorage('login', 'false');
      setValueLocalStorage('login_id', null);
      setValueLocalStorage('login_nome', null);
      setValueLocalStorage('login_cpf', null);
      setValueLocalStorage('login_email', null);
      setValueLocalStorage('login_min_vagas_bat_fechada', null);
      setValueLocalStorage('login_pontos', null);
      setValueLocalStorage('login_conta_assinada', null);

      // Clear login form fields
      clearLoginFields();

      // Update menu visibility
      atualizarVisibilidadeMenu();

      // Close modals
      fecharModal('#myModalCliente');
      fecharModal('#myModalUser');
      fecharModal('#myModalTrocarSenha');
      fecharModal('#myModalReserva');
      fecharModal('#myModal');
      fecharModal('#myModalMinhasReservas');

      // Stop session timer
      stopInactivityTimer();
      
      // Close responsive menu if necessary
      colapsed();

      // Simulate datepicker instance object
      const inst = {};

      // Call onSelectDate function with current date
      if (typeof onSelectDate === 'function') {
        try {
          await onSelectDate(formattedDate, inst);
        } catch (error) {
          console.error('Erro ao executar onSelectDate:', error);
        }
      } else {
        console.error('Função onSelectDate não encontrada!');
      }

      // Dispatch logout event (sem reload - reatividade por eventos)
      try {
        window.dispatchEvent(new CustomEvent('userLogout'));
      } catch {}
      // {|FIM| 13/07/2026 às 11:41 - LIMPAR TOKEN DE CLIENTE NO LOGOUT}
      
    } catch (error) {
      console.error('Erro durante logout:', error);
      // Fallback: limpeza básica sem recarregar a página
      try {
        localStorage.removeItem('kgv_user');
        salvarTokenAutenticacaoCliente(null);
        window.dispatchEvent(new CustomEvent('userLogout'));
      } catch {}
    }
  }

  static async resetPassword(email: string): Promise<RespostaResetSenha> {
    // {|INICIO| 22/07/2026 às 22:50 - ETAPA 7: SOLICITAR RECUPERACAO COM RESPOSTA NEUTRA E SEM SENHA POR E-MAIL}
    if (!email) {
      return {
        status: 'Erro',
        message: 'O e-mail é obrigatório'
      };
    }

    try {
      const url = `${getApiBaseUrl()}/loginservice`;
      
      const headers = new Headers();
      headers.append('Authorization', 'Basic ' + AUTH_SEED);
      headers.append('Content-Type', 'application/json');
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          acao: 'solicitar_reset',
          email: email.trim()
        }),
        redirect: 'follow'
      });

      if (!response.ok) {
        let mensagem = `Erro ao solicitar recuperação: ${response.statusText}`;
        if (response.status === 429) {
          mensagem = 'Muitas tentativas. Aguarde alguns minutos e tente novamente.';
        }

        return {
          status: 'Erro',
          message: mensagem
        };
      }

      const result = await response.json();
      const payload = result?.data ?? result;

      if (result.status === 'success' && String(payload?.status || '').toLowerCase() === 'sucesso') {
        return {
          status: 'Sucesso',
          message: payload?.message || 'Se existir uma conta para este e-mail, enviaremos as instruções de recuperação.'
        };
      }

      return {
        status: 'Erro',
        message: payload?.message || result?.message || 'Erro ao processar solicitação'
      };
    } catch (error) {
      console.error('Reset password error:', error);
      return {
        status: 'Erro',
        message: 'Erro de conexão. Tente novamente.'
      };
    }
    // {|FIM| 22/07/2026 às 22:50 - ETAPA 7: SOLICITAR RECUPERACAO COM RESPOSTA NEUTRA E SEM SENHA POR E-MAIL}
  }

  static async validateResetToken(token: string, newPassword: string): Promise<RespostaTokenResetSenha> {
    // {|INICIO| 22/07/2026 às 22:50 - ETAPA 7: REDEFINIR SENHA PELO LOGINERVICE USANDO TOKEN DE USO UNICO}
    if (!token || !newPassword) {
      throw new Error('Token e nova senha são obrigatórios');
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/loginservice`, {
        method: 'POST',
        headers: {
          Authorization: `Basic ${AUTH_SEED}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          acao: 'redefinir_senha_com_token',
          token,
          novaSenha: newPassword
        }),
      });

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status}`);
      }

      const result = await response.json();
      const payload = result?.data ?? result;
      
      if (result?.status === 'success' && String(payload?.status || '').toLowerCase() === 'sucesso') {
        return {
          status: 'success',
          message: payload?.message || 'Senha alterada com sucesso!'
        };
      }

      throw new Error(payload?.message || result?.message || 'Erro ao validar token');
    } catch (error) {
      console.error('Validate reset token error:', error);
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Erro de conexão. Tente novamente.');
    }
    // {|FIM| 22/07/2026 às 22:50 - ETAPA 7: REDEFINIR SENHA PELO LOGINERVICE USANDO TOKEN DE USO UNICO}
  }

  static async checkResetToken(token: string): Promise<RespostaTokenResetSenha> {
    // {|INICIO| 22/07/2026 às 22:50 - ETAPA 7: VALIDAR TOKEN DE RESET NO BACKEND REAL}
    if (!token) {
      return {
        status: 'error',
        valid: false,
        message: 'Token não encontrado!'
      };
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/loginservice`, {
        method: 'POST',
        headers: {
          Authorization: `Basic ${AUTH_SEED}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          acao: 'validar_token_reset',
          token
        }),
      });

      if (!response.ok) {
        return {
          status: 'error',
          valid: false,
          message: 'Token inválido ou expirado!'
        };
      }

      const result = await response.json();
      const payload = result?.data ?? result;
      return {
        status: result?.status === 'success' ? 'success' : 'error',
        valid: payload?.valid === true,
        message: payload?.message || 'Token inválido ou expirado!'
      };
    } catch (error) {
      console.error('Check reset token error:', error);
      return {
        status: 'error',
        valid: false,
        message: 'Erro ao verificar token. Tente novamente.'
      };
    }
    // {|FIM| 22/07/2026 às 22:50 - ETAPA 7: VALIDAR TOKEN DE RESET NO BACKEND REAL}
  }

  static async consultarPontos(userId: number): Promise<{ pontos: number }> {
    try {
      const url = `${getApiBaseUrl()}/consultarpontos?id=${userId}`;
      
      const headers = new Headers();
      // {|INICIO| 23/07/2026 às 09:48 - AUTH: PRIORIZAR BEARER NA CONSULTA LEGADA DE PONTOS}
      headers.append('Authorization', montarValorAutorizacao(AUTH_SEED));
      // {|FIM| 23/07/2026 às 09:48 - AUTH: PRIORIZAR BEARER NA CONSULTA LEGADA DE PONTOS}
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
        redirect: 'follow'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await parseJsonResponse(response);
      
      if (result.data && result.data.status === 'Sucesso') {
        return {
          pontos: result.data.pontos || 0
        };
      } else {
        throw new Error(result.data?.message || 'Erro ao consultar pontos');
      }
    } catch (error) {
      console.error('Consultar pontos error:', error);
      throw new Error('Erro ao consultar pontos. Tente novamente.');
    }
  }
}
